import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Breadcrumb from "../../components/breadcrumb/index";
import Layouts from "../../layouts/layouts";
import Select from "react-select";

export default function Create_asset() {
  const title = "Add Assets";
  const navigate = useNavigate();

  // ============================================================
  // DROPDOWN DATA
  // ============================================================

  const [users, setUsers] = useState([]);
  const [assets, setAssets] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [hubs, setHubs] = useState([]);

  // ============================================================
  // FORM DATA
  // ============================================================

  const [formData, setFormData] = useState({
    assetname: "",
    company: "",
    id_assettype: "",

    id_user: "",

    assignFromDate: "",
    assignToDate: "",

    vehicle: "",
    id_hub: "",

    approved: false,
    approvedBy: "",

    quantity: 1,

    description: "",
  });

  // ============================================================
  // OTHER STATES
  // ============================================================

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // ============================================================
  // BREADCRUMB
  // ============================================================

  const brad = [
    {
      name: "Home",
    },
    {
      name: title,
    },
  ];

  // ============================================================
  // FETCH USERS
  // ============================================================

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = localStorage.getItem("token");

        if (!token) {
          console.error("No token found");
          return;
        }

        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/v1/admin/users`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        setUsers(response.data.users || []);
      } catch (error) {
        console.error("Error fetching users:", error);
        setUsers([]);
      }
    };

    fetchUsers();
  }, []);

  // ============================================================
  // FETCH ALL ASSET TYPES
  // ============================================================

  useEffect(() => {
    const fetchAssetTypes = async () => {
      try {
        const token = localStorage.getItem("token");

        if (!token) {
          console.error("No token found");
          return;
        }

        const params = new URLSearchParams();
        params.append("page", 1);
        params.append("limit", 100000);

        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/v1/asset-types?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        console.log("Asset Types Response:", response.data);

        const assetTypeGroups = Array.isArray(response.data.assetTypes)
          ? response.data.assetTypes
          : [];

        // Extract all asset entries from all groups
        const assetTypeEntries = assetTypeGroups.flatMap((group) =>
          (Array.isArray(group.entries) ? group.entries : []).map((entry) => ({
            ...entry,
            assetname: entry.assetname || group.assetname || group._id,
          })),
        );

        setAssets(assetTypeEntries);
      } catch (error) {
        console.error(
          "Error fetching asset types:",
          error.response?.data || error.message,
        );

        setAssets([]);
      }
    };

    fetchAssetTypes();
  }, []);

  // ============================================================
  // FETCH VEHICLES
  // ============================================================

  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        const token = localStorage.getItem("token");

        if (!token) {
          console.error("No token found");
          return;
        }

        /*
         * If your project uses a different vehicle API,
         * replace this endpoint.
         */
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/v1/vehicles`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const vehicleData = response.data.vehicles || response.data.data || [];

        setVehicles(vehicleData);
      } catch (error) {
        console.error("Error fetching vehicles:", error);

        setVehicles([]);
      }
    };

    fetchVehicles();
  }, []);

  // ============================================================
  // FETCH HUBS
  // ============================================================

  useEffect(() => {
    const fetchHubs = async () => {
      try {
        const token = localStorage.getItem("token");

        if (!token) {
          console.error("No token found");
          return;
        }

        /*
         * If your project uses a different hub API,
         * replace this endpoint.
         */
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/v1/hubs`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const hubData = response.data.hubs || response.data.data || [];

        setHubs(hubData);
      } catch (error) {
        console.error("Error fetching hubs:", error);

        setHubs([]);
      }
    };

    fetchHubs();
  }, []);

  // ============================================================
  // UNIQUE ASSET TYPE OPTIONS
  // ============================================================

  const assetTypeOptions = [
    ...new Map(
      assets
        .filter((asset) => asset?.assetname)
        .map((asset) => [
          asset.assetname,
          {
            value: asset.assetname,
            label: asset.assetname,
          },
        ]),
    ).values(),
  ];

  // ============================================================
  // SELECTED ASSET TYPE
  // ============================================================

  const selectedAssetRecords = assets.filter(
    (asset) => asset?.assetname === formData.assetname,
  );

  // ============================================================
  // COMPANY OPTIONS
  // ============================================================

  const companyOptions = [
    ...new Map(
      selectedAssetRecords
        .filter((asset) => asset?.company)
        .map((asset) => [
          asset.company,
          {
            value: asset.company,
            label: asset.company,
          },
        ]),
    ).values(),
  ];

  // ============================================================
  // SELECTED ASSET RECORD
  // ============================================================

  const selectedAssetRecord = assets.find(
    (asset) =>
      asset?.assetname === formData.assetname &&
      asset?.company === formData.company,
  );

  // ============================================================
  // AVAILABLE QUANTITY
  // ============================================================

  const availableQuantity = Number(
    selectedAssetRecord?.available ??
      Math.max(
        Number(selectedAssetRecord?.quantity || 0) -
          Number(selectedAssetRecord?.used || 0),
        0,
      ),
  );

  // ============================================================
  // USER OPTIONS
  // ============================================================

  const userOptions = users.map((user) => ({
    value: user._id,
    label: `${user.name || "Unknown Name"}${
      user.phonenumber ? ` (${user.phonenumber})` : ""
    }`,
  }));

  // ============================================================
  // VEHICLE OPTIONS
  // ============================================================

  const vehicleOptions = vehicles.map((vehicle) => ({
    value: vehicle._id,
    label:
      vehicle.numberplate ||
      vehicle.vehicleNumber ||
      vehicle.name ||
      "Unknown Vehicle",
  }));

  // ============================================================
  // HUB OPTIONS
  // ============================================================

  const hubOptions = hubs.map((hub) => ({
    value: hub._id,
    label: hub.name || hub.hubname || hub.hubName || "Unknown Hub",
  }));

  // ============================================================
  // APPROVED BY OPTIONS
  // ============================================================

  const approvedByOptions = users.map((user) => ({
    value: user._id,
    label: `${user.name || "Unknown Name"}${
      user.phonenumber ? ` (${user.phonenumber})` : ""
    }`,
  }));

  // ============================================================
  // ASSET TYPE CHANGE
  // ============================================================

  const handleAssetTypeChange = (selectedOption) => {
    const assetname = selectedOption ? selectedOption.value : "";

    setFormData((prev) => ({
      ...prev,

      assetname,

      // Reset company when asset type changes
      company: "",

      // Reset actual AssetType ID
      id_assettype: "",

      // Reset quantity
      quantity: 1,
    }));

    setErrors((prev) => ({
      ...prev,
      assetname: "",
      company: "",
      id_assettype: "",
      quantity: "",
    }));
  };

  // ============================================================
  // COMPANY CHANGE
  // ============================================================

  const handleCompanyChange = (selectedOption) => {
    const company = selectedOption ? selectedOption.value : "";

    /*
     * Find the exact AssetType record
     * based on:
     *
     * Asset Name + Company
     *
     * Example:
     *
     * Laptop + Dell
     *
     * returns the actual MongoDB AssetType
     * document.
     */

    const selectedAsset = assets.find(
      (asset) =>
        asset?.assetname === formData.assetname && asset?.company === company,
    );

    setFormData((prev) => ({
      ...prev,

      company,

      // Actual AssetType MongoDB ID
      id_assettype: selectedAsset?._id || "",

      // Reset quantity
      quantity: 1,
    }));

    setErrors((prev) => ({
      ...prev,
      company: "",
      id_assettype: "",
      quantity: "",
    }));
  };

  // ============================================================
  // GENERIC SELECT CHANGE
  // ============================================================

  const handleSelectChange = (selectedOption, { name }) => {
    setFormData((prev) => ({
      ...prev,

      [name]: selectedOption ? selectedOption.value : "",
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  // ============================================================
  // INPUT CHANGE
  // ============================================================

  const onChangeHandler = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  // ============================================================
  // APPROVED CHANGE
  // ============================================================

  const handleApprovedChange = (event) => {
    const approved = event.target.checked;

    setFormData((prev) => ({
      ...prev,

      approved,

      approvedBy: approved ? prev.approvedBy : "",
    }));

    setErrors((prev) => ({
      ...prev,
      approvedBy: "",
    }));
  };

  // ============================================================
  // VALIDATION
  // ============================================================

  const validateForm = () => {
    const newErrors = {};

    if (!formData.assetname) {
      newErrors.assetname = "Asset Type is required.";
    }

    if (!formData.company) {
      newErrors.company = "Company is required.";
    }

    if (!formData.id_assettype) {
      newErrors.id_assettype = "Please select a valid Asset Type and Company.";
    }

    if (!formData.id_user) {
      newErrors.id_user = "User is required.";
    }

    if (!formData.assignFromDate) {
      newErrors.assignFromDate = "Assign From date is required.";
    }

    if (
      formData.assignToDate &&
      formData.assignFromDate &&
      new Date(formData.assignToDate) < new Date(formData.assignFromDate)
    ) {
      newErrors.assignToDate =
        "Assign To date cannot be before Assign From date.";
    }

    if (!formData.quantity || Number(formData.quantity) <= 0) {
      newErrors.quantity = "Quantity must be greater than 0.";
    }

    if (
      availableQuantity >= 0 &&
      Number(formData.quantity) > availableQuantity
    ) {
      newErrors.quantity = `Only ${availableQuantity} ${
        availableQuantity === 1 ? "unit" : "units"
      } available.`;
    }

    if (formData.approved && !formData.approvedBy) {
      newErrors.approvedBy = "Approved By is required when asset is approved.";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  // ============================================================
  // SUBMIT
  // ============================================================

  const onSubmitHandler = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    const token = localStorage.getItem("token");

    if (!token) {
      console.error("No token found");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        id_user: formData.id_user,

        id_assettype: formData.id_assettype,

        assignFromDate: formData.assignFromDate,

        assignToDate: formData.assignToDate || null,

        vehicle: formData.vehicle || null,

        approved: Boolean(formData.approved),

        approvedBy: formData.approved ? formData.approvedBy || null : null,

        company: formData.company || "",

        quantity: Number(formData.quantity),

        id_hub: formData.id_hub || null,

        description: formData.description || "",
      };

      console.log("Creating Asset:", payload);

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/v1/assets`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      console.log("Create Asset Response:", response.data);

      if (response.status === 201 || response.status === 200) {
        navigate("/asset");
      }
    } catch (error) {
      console.error(
        "Error creating asset:",
        error.response?.data || error.message,
      );

      setErrors((prev) => ({
        ...prev,
        submit:
          error.response?.data?.message ||
          "Unable to create asset. Please try again.",
      }));
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // SELECT STYLES
  // ============================================================

  const selectStyles = {
    control: (provided) => ({
      ...provided,

      minHeight: "38px",

      borderColor: "#ced4da",

      boxShadow: "none",

      "&:hover": {
        borderColor: "#86b7fe",
      },
    }),
  };

  // ============================================================
  // UI
  // ============================================================

  return (
    <>
      <Layouts />

      <div className="container-fluid">
        <Breadcrumb title={title} brad={brad} />

        <form
          className="card"
          style={{
            borderTop: "2px solid #4723d9",
          }}
          onSubmit={onSubmitHandler}
        >
          {/* ==================================================
              HEADER
          =================================================== */}

          <div
            className="card-header d-flex justify-content-between align-items-center border-bottom"
            style={{
              padding: "10px 16px",
            }}
          >
            <h5
              className="mb-0"
              style={{
                fontWeight: "400",
                color: "#343a40",
              }}
            >
              {title}
            </h5>

            <button
              type="button"
              className="btn btn-info btn-sm text-white"
              onClick={() => navigate(-1)}
            >
              Back
            </button>
          </div>

          {/* ==================================================
              BODY
          =================================================== */}

          <div className="card-body">
            <div className="row">
              {/* ==============================================
                  ASSET TYPE
              =============================================== */}

              <div className="col-md-6 mt-3">
                <label>
                  <b>Asset Type *</b>
                </label>

                <Select
                  name="assetname"
                  options={assetTypeOptions}
                  value={
                    assetTypeOptions.find(
                      (option) => option.value === formData.assetname,
                    ) || null
                  }
                  onChange={handleAssetTypeChange}
                  placeholder="Select Asset Type"
                  isClearable
                  styles={selectStyles}
                  classNamePrefix="select"
                />

                {errors.assetname && (
                  <div className="text-danger mt-1">{errors.assetname}</div>
                )}
              </div>

              {/* ==============================================
                  COMPANY
              =============================================== */}

              <div className="col-md-6 mt-3">
                <label>
                  <b>Company *</b>
                </label>

                <Select
                  name="company"
                  options={companyOptions}
                  value={
                    companyOptions.find(
                      (option) => option.value === formData.company,
                    ) || null
                  }
                  onChange={handleCompanyChange}
                  placeholder={
                    formData.assetname
                      ? "Select Company"
                      : "Select Asset Type first"
                  }
                  isClearable
                  isDisabled={!formData.assetname}
                  styles={selectStyles}
                  classNamePrefix="select"
                />

                {errors.company && (
                  <div className="text-danger mt-1">{errors.company}</div>
                )}
              </div>

              {/* ==============================================
                  AVAILABLE QUANTITY
              =============================================== */}

              {formData.id_assettype && (
                <div className="col-12 mt-3">
                  <div
                    className="alert alert-info mb-0 py-2"
                    style={{
                      fontSize: "13px",
                    }}
                  >
                    <strong>Available Quantity:</strong> {availableQuantity}
                  </div>
                </div>
              )}

              {/* ==============================================
                  USER
              =============================================== */}

              <div className="col-md-6 mt-3">
                <label>
                  <b>User Name *</b>
                </label>

                <Select
                  name="id_user"
                  options={userOptions}
                  value={
                    userOptions.find(
                      (option) => option.value === formData.id_user,
                    ) || null
                  }
                  onChange={handleSelectChange}
                  placeholder="Select User"
                  isClearable
                  styles={selectStyles}
                  classNamePrefix="select"
                />

                {errors.id_user && (
                  <div className="text-danger mt-1">{errors.id_user}</div>
                )}
              </div>

              {/* ==============================================
                  VEHICLE
              =============================================== */}

              <div className="col-md-6 mt-3">
                <label>
                  <b>Vehicle</b>
                </label>

                <Select
                  name="vehicle"
                  options={vehicleOptions}
                  value={
                    vehicleOptions.find(
                      (option) => option.value === formData.vehicle,
                    ) || null
                  }
                  onChange={handleSelectChange}
                  placeholder="Select Vehicle"
                  isClearable
                  styles={selectStyles}
                  classNamePrefix="select"
                />
              </div>

              {/* ==============================================
                  HUB
              =============================================== */}

              <div className="col-md-6 mt-3">
                <label>
                  <b>Hub</b>
                </label>

                <Select
                  name="id_hub"
                  options={hubOptions}
                  value={
                    hubOptions.find(
                      (option) => option.value === formData.id_hub,
                    ) || null
                  }
                  onChange={handleSelectChange}
                  placeholder="Select Hub"
                  isClearable
                  styles={selectStyles}
                  classNamePrefix="select"
                />
              </div>

              {/* ==============================================
                  ASSIGN FROM
              =============================================== */}

              <div className="col-md-6 mt-3">
                <label>
                  <b>Assign From *</b>
                </label>

                <input
                  type="date"
                  name="assignFromDate"
                  value={formData.assignFromDate}
                  onChange={onChangeHandler}
                  className="form-control"
                />

                {errors.assignFromDate && (
                  <div className="text-danger mt-1">
                    {errors.assignFromDate}
                  </div>
                )}
              </div>

              {/* ==============================================
                  ASSIGN TO
              =============================================== */}

              <div className="col-md-6 mt-3">
                <label>
                  <b>Assign To</b>
                </label>

                <input
                  type="date"
                  name="assignToDate"
                  value={formData.assignToDate}
                  onChange={onChangeHandler}
                  className="form-control"
                />

                {errors.assignToDate && (
                  <div className="text-danger mt-1">{errors.assignToDate}</div>
                )}
              </div>

              {/* ==============================================
                  QUANTITY
              =============================================== */}

              <div className="col-md-6 mt-3">
                <label>
                  <b>Quantity *</b>
                </label>

                <input
                  type="number"
                  name="quantity"
                  min="1"
                  max={availableQuantity > 0 ? availableQuantity : undefined}
                  placeholder="Enter Quantity"
                  className="form-control"
                  value={formData.quantity}
                  onChange={onChangeHandler}
                  disabled={!formData.id_assettype}
                />

                {formData.id_assettype && (
                  <small className="text-muted">
                    Maximum available: {availableQuantity}
                  </small>
                )}

                {errors.quantity && (
                  <div className="text-danger mt-1">{errors.quantity}</div>
                )}
              </div>

              {/* ==============================================
                  APPROVED
              =============================================== */}

              <div className="col-md-6 mt-3">
                <label className="d-block">
                  <b>Approved</b>
                </label>

                <div className="form-check form-switch mt-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="approved"
                    checked={formData.approved}
                    onChange={handleApprovedChange}
                  />

                  <label className="form-check-label" htmlFor="approved">
                    {formData.approved ? "Yes" : "No"}
                  </label>
                </div>
              </div>

              {/* ==============================================
                  APPROVED BY
              =============================================== */}

              <div className="col-md-6 mt-3">
                <label>
                  <b>Approved By</b>
                </label>

                <Select
                  name="approvedBy"
                  options={approvedByOptions}
                  value={
                    approvedByOptions.find(
                      (option) => option.value === formData.approvedBy,
                    ) || null
                  }
                  onChange={handleSelectChange}
                  placeholder="Select Approved By"
                  isClearable
                  isDisabled={!formData.approved}
                  styles={selectStyles}
                  classNamePrefix="select"
                />

                {errors.approvedBy && (
                  <div className="text-danger mt-1">{errors.approvedBy}</div>
                )}
              </div>

              {/* ==============================================
                  DESCRIPTION
              =============================================== */}

              <div className="col-12 mt-3">
                <label>
                  <b>Description</b>
                </label>

                <textarea
                  name="description"
                  placeholder="Enter Description"
                  rows="4"
                  className="form-control"
                  value={formData.description}
                  onChange={onChangeHandler}
                />
              </div>

              {/* ==============================================
                  SUBMIT ERROR
              =============================================== */}

              {errors.submit && (
                <div className="col-12 mt-3">
                  <div className="alert alert-danger mb-0">{errors.submit}</div>
                </div>
              )}
            </div>

            {/* ==================================================
                BUTTONS
            =================================================== */}

            <div className="d-flex justify-content-end gap-2 mt-4">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => navigate(-1)}
                disabled={loading}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="btn btn-primary btn-sm"
                disabled={loading}
              >
                {loading ? "Saving..." : "Add Asset"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}
